import { initializeApp, getApps, FirebaseApp } from 'firebase/app'
import { getAuth, GoogleAuthProvider, Auth } from 'firebase/auth'
import { getFirestore, Firestore } from 'firebase/firestore'
import { getStorage, FirebaseStorage } from 'firebase/storage'
import { getFunctions, Functions, httpsCallable } from 'firebase/functions'

const getFirebaseConfig = () => ({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
})

// Lazy initialization
let _app: FirebaseApp | null = null
let _auth: Auth | null = null
let _db: Firestore | null = null
let _storage: FirebaseStorage | null = null
let _functions: Functions | null = null

function initializeIfNeeded(): FirebaseApp {
  if (!_app) {
    const config = getFirebaseConfig()
    if (!config.apiKey) {
      throw new Error('Firebase API key not configured. Make sure VITE_FIREBASE_API_KEY is set.')
    }
    _app = getApps().length === 0 ? initializeApp(config) : getApps()[0]
  }
  return _app
}

export function getFirebaseApp(): FirebaseApp {
  return initializeIfNeeded()
}

export function getFirebaseAuth(): Auth {
  if (!_auth) {
    _auth = getAuth(initializeIfNeeded())
  }
  return _auth
}

export function getFirebaseDb(): Firestore {
  if (!_db) {
    const databaseId = import.meta.env.VITE_FIREBASE_DATABASE_ID || 'customer-assistant-dev'
    _db = getFirestore(initializeIfNeeded(), databaseId)
  }
  return _db
}

export function getFirebaseStorage(): FirebaseStorage {
  if (!_storage) {
    _storage = getStorage(initializeIfNeeded())
  }
  return _storage
}

export function getFirebaseFunctions(): Functions {
  if (!_functions) {
    _functions = getFunctions(initializeIfNeeded(), 'us-central1')
  }
  return _functions
}

export function getGoogleProvider(): GoogleAuthProvider {
  return new GoogleAuthProvider()
}

// Helper to create callable function
export function getCallableFunction<T, R>(name: string) {
  return httpsCallable<T, R>(getFirebaseFunctions(), name)
}

// Named exports for convenience
export const auth = getFirebaseAuth
export const db = getFirebaseDb
export const storage = getFirebaseStorage
export const functions = getFirebaseFunctions
export const googleProvider = getGoogleProvider
